import React, { useState } from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';
import PortfolioChart from './components/PortfolioChart';
import AssetAllocation from './components/AssetAllocation';
import { Asset, CalculatedMetrics } from './DesktopMyAssets';

interface MobileMyAssetsProps {
    assets: Asset[];
    calculatedMetrics: CalculatedMetrics | null;
    portfolioHistory: { time: string; value: number }[];
}

const MobileMyAssets: React.FC<MobileMyAssetsProps> = ({ assets, calculatedMetrics, portfolioHistory }) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'allocation' | 'assets'>('overview');

    // Default sorting for assets on mobile: by weight (value)
    const sortedAssets = [...assets].sort((a, b) => b.valeur_actuelle - a.valeur_actuelle);

    const isPositiveTotal = calculatedMetrics ? calculatedMetrics.totalPL >= 0 : true;
    const isPositiveDay = calculatedMetrics ? calculatedMetrics.dayPL >= 0 : true;

    return (
        <div style={{ paddingBottom: '30px', background: 'var(--bg-main)' }}>
            <Disclaimer type="simulation" />

            {/* Hero Header (replaces PortfolioSummary grid) */}
            {calculatedMetrics && (
                <div style={{ padding: '24px 20px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 'bold' }}>VALEUR DU PORTEFEUILLE</div>
                    <div style={{ fontSize: '36px', fontWeight: '900', color: 'var(--text-main)', letterSpacing: '-1px' }}>
                        {calculatedMetrics.portfolioValue.toLocaleString('fr-FR')} <span style={{ fontSize: '16px', fontWeight: '600' }}>FCFA</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        + {calculatedMetrics.cashBalance.toLocaleString('fr-FR')} FCFA en espèces = {calculatedMetrics.totalValue.toLocaleString('fr-FR')} FCFA au total
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                        <div style={{ background: isPositiveDay ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '6px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Aujourd'hui</span>
                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: isPositiveDay ? '#10B981' : '#F43F5E' }}>
                                {isPositiveDay ? '+' : ''}{calculatedMetrics.dayPL.toLocaleString('fr-FR')} ({calculatedMetrics.dayPLPct.toFixed(2)}%)
                            </span>
                        </div>
                        <div style={{ background: isPositiveTotal ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '6px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Total</span>
                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: isPositiveTotal ? '#10B981' : '#F43F5E' }}>
                                {isPositiveTotal ? '+' : ''}{calculatedMetrics.totalPL.toLocaleString('fr-FR')} ({calculatedMetrics.totalPLPct.toFixed(2)}%)
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Segmented Control */}
            <div style={{ padding: '16px 20px', position: 'sticky', top: '0', background: 'var(--bg-main)', zIndex: 10, borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', background: 'var(--bg-input)', borderRadius: '12px', padding: '4px' }}>
                    {(['overview', 'allocation', 'assets'] as const).map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                flex: 1, padding: '10px 0', borderRadius: '10px', fontSize: '13px', fontWeight: '600',
                                background: activeTab === tab ? '#3b82f6' : 'transparent', color: activeTab === tab ? '#ffffff' : 'var(--text-muted)',
                                border: 'none', transition: 'all 0.2s', touchAction: 'manipulation'
                            }}
                        >
                            {tab === 'overview' ? 'Performance' : tab === 'allocation' ? 'Répartition' : 'Actifs'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div style={{ padding: '20px' }}>
                {activeTab === 'overview' && (
                    <Box>
                        <div style={{ padding: '16px 0' }}>
                            <PortfolioChart data={portfolioHistory} />
                        </div>
                    </Box>
                )}

                {activeTab === 'allocation' && (
                    <Box>
                        <div style={{ padding: '16px 0' }}>
                            <AssetAllocation assets={assets} />
                        </div>
                    </Box>
                )}

                {activeTab === 'assets' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>Vos Positions ({assets.length})</div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {sortedAssets.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>Vous n'avez pas encore d'actions.</div>
                            ) : (
                                sortedAssets.map((asset, index) => {
                                    const isPosDay = asset.variation >= 0;
                                    const isPosTotal = asset.plus_minus_value >= 0;
                                    // Calculate weight
                                    const weight = calculatedMetrics?.portfolioValue ? ((asset.valeur_actuelle / calculatedMetrics.portfolioValue) * 100).toFixed(1) : '0';

                                    return (
                                        <Box key={index}>
                                            <div style={{ padding: '16px' }}>
                                                {/* Top Row: Symbol & Current Value */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'var(--text-main)', fontSize: '14px' }}>
                                                            {asset.symbole.substring(0, 2)}
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>{asset.symbole}</div>
                                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '120px' }}>{asset.nom_societe || asset.symbole}</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main)' }}>{asset.valeur_actuelle.toLocaleString('fr-FR')}</div>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Poids: {weight}%</div>
                                                    </div>
                                                </div>

                                                {/* Middle Row: Quantity/Price info */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)', padding: '10px 12px', borderRadius: '8px', marginBottom: '12px' }}>
                                                    <div>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Qté</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{asset.quantite}</div>
                                                    </div>
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>PRU</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{asset.prix_acquisition.toLocaleString('fr-FR')}</div>
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Prix (FCFA)</div>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{asset.cours_actuel.toLocaleString('fr-FR')}</div>
                                                    </div>
                                                </div>

                                                {/* Bottom Row: Performance PnL */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Aujourd'hui</span>
                                                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: isPosDay ? '#10B981' : '#F43F5E' }}>
                                                            {isPosDay ? '+' : ''}{asset.variation.toFixed(2)}%
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Plus-Value Totale</span>
                                                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: isPosTotal ? '#10B981' : '#F43F5E' }}>
                                                            {isPosTotal ? '+' : ''}{asset.plus_minus_value.toLocaleString('fr-FR')} ({asset.performance.toFixed(2)}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Box>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileMyAssets;
